import { getYear } from 'date-fns';
import { get, startCase } from 'lodash';
import React, { Component } from 'react';
import Helmet from 'react-helmet';
import { Location } from '@reach/router';
import { STATES } from  '@upsolve/shared';
import config from '../../../site-config';
import pathToBreadcrumb from './pathToBreadcrumb';
import breadcrumbToJSONLD from './breadcrumbToJSONLD';
import howToToJSONLD from './howToToJSONLD';
import videoToJSONLD from './videoToJSONLD';
import articleToJSONLD from './articleToJSONLD';
import faqToJSONLD from './faqToJSONLD';
import getFaqFromRichText from '../Contentful/getFaqFromRichText';
import getHowToFromRichText from '../Contentful/getHowToFromRichText';
import getLegalServicesFromRichText from '../Contentful/getLegalServicesFromRichText';
import imagesMappedToUrls from './imagesMappedToUrls';
import richTextNodeToString from '../Contentful/richTextNodeToString'
import {
  CITY_PAGE, CITY_SUB_PAGE, CITY_ATTORNEY_PRICING, STATE_PAGE, STATE_SUB_PAGE,
  STATE_BANKRUPTCY_FORMS, STATE_MEANS_TEST,
} from '../Programmatic/pageTypes';
import privatePricings from '../../../content/privatePricings.json';
import ratingsJSONLD from './ratingsJSONLD';

const SEOChild = ({ contentNode, contentPath, noIndex, pageNode, seoNode, ...props }) => {
  const path = props.location.pathname;
  let {
    description,
    image,
    robots,
    title,
    url,
  } = props;
  // JSONLD Vars
  let jsonLD = [];
  let ldMapFaq = null;
  let ldMapHowTo = null;
  let ldMapArticle = null;
  let ldMapLegalServices = null;
  let ldMapVideo = null;
  let ldMapRatings = false;

  // SETUP
  // - Setup from SEO Node Model
  if (seoNode != null) {
    title = seoNode.title;
    description = seoNode.description;
    image = imagesMappedToUrls(seoNode.images || [])[0] || 'https://upsolve.org/images/meta-legalaid.png';

    if (seoNode.structuredType === 'howTo') {
      ldMapHowTo = howToToJSONLD(seoNode);
    }
    if (seoNode.structuredType === 'faq') {
      ldMapFaq = faqToJSONLD(seoNode);
    }
    if (seoNode.structuredType === 'article') {
      // OPTIMIZE: somehow articles crash build
      ldMapArticle = articleToJSONLD({ path, seo: seoNode });
    }
    if (seoNode.video != null) {
      // OPTIMIZE: I think this is ok, but just disabling to try deploying
      ldMapVideo = videoToJSONLD(seoNode);
    }
    if (seoNode.includeRatings === true) {
      // OPTIMIZE: too many ratings crashes build
      ldMapRatings = true;
    }

    // - Setup from Page model if SEO node wasn't present
  } else if (pageNode != null) {
    const cityText = startCase((get(pageNode, 'locality.city') || ''));
    const stateText = STATES[(get(pageNode, 'locality.state') || '').toUpperCase()];
    const thisYear = getYear(new Date());
    if (pageNode.type === STATE_PAGE) {
      title = `How to File Bankruptcy for Free in ${stateText} (${thisYear} Guide)`;
      description = `Live in ${stateText} and need help filing for bankruptcy and can't afford an attorney? Our legal aid nonprofit guides ${stateText} debtors through the chapter 7 process.`;
      ldMapRatings = true;
    }
    if (pageNode.type === STATE_SUB_PAGE) {
      title = pageNode.title;
      description = get(pageNode, 'content.json') != null
        ? richTextNodeToString(get(pageNode, 'summary.json') || get(pageNode, 'content.json'), 155)
        : config.siteDescription;
    }
    if (pageNode.type === STATE_BANKRUPTCY_FORMS) {
      title = `${thisYear} Bankruptcy Forms for ${stateText}`;
      description = `Want to file Chapter 7 bankruptcy? You're going to want to understand what the free bankruptcy forms for ${stateText}.`;
    }
    if (pageNode.type === STATE_MEANS_TEST) {
      title = `${stateText} Means Test ${thisYear}`;
      description = `The Means Test determines your eligibility for Chapter 7 bankruptcy in ${stateText}. Upsolve gives free help to those who are eligible and low income.`;
    }
    if (pageNode.type === CITY_PAGE) {
      title = `File Bankruptcy for Free in ${cityText} ${stateText} (${thisYear} Guide)`;
      description = `Live in ${cityText} ${stateText} and need help filing for bankruptcy and can't afford an attorney? Our legal aid nonprofit guides ${cityText} ${stateText} debtors through the chapter 7 process.`;
      // OPTIMIZE: too many ratings crashes build
      ldMapRatings = true;
    }
    if (pageNode.type === CITY_SUB_PAGE) {
      title = pageNode.title;
      description = (get(pageNode, 'summary.json') || get(pageNode, 'content.json')) != null
        ? richTextNodeToString(get(pageNode, 'summary.json') || get(pageNode, 'content.json'), 155)
        : config.siteDescription;
    }
    if (pageNode.type === CITY_ATTORNEY_PRICING) {
      title = `Cost of ${cityText} ${stateText} Bankruptcy Attorney (${thisYear})`;
      description = `Bankruptcy attorneys in ${cityText} ${stateText} cost between $${Number(privatePricings[get(pageNode, 'locality.state', '').toUpperCase()].low).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} and $${Number(privatePricings[get(pageNode, 'locality.state', '').toUpperCase()].high).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} according to our research. Upsolve is a nonprofit giving free assistance for chapter 7 bankruptcies.`;
    }
    if (title == null) title = pageNode.title;

    // - Setup from Content Node Model (legacy of Learn Articles)
  } else if (contentNode != null) {
    title = contentNode.title || contentNode.seoTitle;
    description = contentNode.seoDescription;
    image = get(contentNode, 'seoImage.image.file.url')
      || 'https://static.upsolve.org/app-preview-bubble.png';
    if (/^\/\//.test(image)) image = image.replace(/^\/\//, 'https://');
    ldMapArticle = articleToJSONLD({
      url: `https://upsolve.org${path}`,
      content: contentNode,
    });
    ldMapRatings = true;
    // Video
    if (contentNode.video != null) {
      ldMapVideo = videoToJSONLD({
        description: contentNode.description,
        video: contentNode.video,
        createdAt: get(contentNode.video, 'uploadedAt') || contentNode.createdAt,
      });
    }

  // - Setup from props
  } else {
    if (image == null) image = 'https://static.upsolve.org/app-preview-bubble.png';
    if (jsonLD.length === 0) jsonLD = Array.isArray(props.jsonLD) ? props.jsonLD : [];
    if (props.includeRating) jsonLD.push(ratingsJSONLD);
  }

  // Index overrides
  if (noIndex === true) robots = 'NONE';

  // Defaults if none set
  if (title == null) title = config.siteTitle;
  if (description == null) description = config.siteDescription;
  if (image == null) image = 'https://upsolve.org/images/meta-legalaid.png';
  if (url == null && path != null) url = `https://upsolve.org${path}`;

  // Add Upsolve to title if none included
  if (/upsolve/gi.test(title || '') === false && title.length <= 49) {
    title = `${title} | Upsolve`;
  }

  // JSON+LD
  // - breadcrumb
  if (path != null && jsonLD.find(j => j['@type'] === 'BreadcrumbList') == null) {
    if (pathToBreadcrumb(path).length > 0) jsonLD.push(breadcrumbToJSONLD(pathToBreadcrumb(path)));
  }
  // - extract from content
  if (get(pageNode, 'content.json') != null) {
    if (getHowToFromRichText(get(pageNode, 'content.json'), { baseUrl: path }) != null) {
      ldMapHowTo = howToToJSONLD({ howTo: getHowToFromRichText(get(pageNode, 'content.json'), { baseUrl: path }), page: pageNode });
    }
    if (getFaqFromRichText(get(pageNode, 'content.json'), { baseUrl: path }) != null) {
      ldMapFaq = faqToJSONLD({
        faq: getFaqFromRichText(get(pageNode, 'content.json'), { baseUrl: path }),
      });
    }
    if (getLegalServicesFromRichText(get(pageNode, 'content.json'), { baseUrl: path }).length > 0) {
      ldMapLegalServices = getLegalServicesFromRichText(get(pageNode, 'content.json'), { baseUrl: path });
    }
  }
  // - backups
  if (pageNode != null) {
    if (ldMapFaq == null && ldMapHowTo == null) ldMapArticle = articleToJSONLD({ path, page: pageNode });
  }
  // - compile
  if (ldMapFaq != null && jsonLD.find(j => j['@type'] === 'FAQPage') == null) jsonLD.push(ldMapFaq);
  if (ldMapHowTo != null && jsonLD.find(j => j['@type'] === 'HowTo') == null) jsonLD.push(ldMapHowTo);
  if (ldMapArticle != null && jsonLD.find(j => j['@type'] === 'Article') == null) jsonLD.push(ldMapArticle);
  if (ldMapVideo != null && jsonLD.find(j => j['@type'] === 'VideoObject') == null) jsonLD.push(ldMapVideo);
  if (ldMapLegalServices != null) jsonLD.push(...ldMapLegalServices);
  if (ldMapRatings === true || (
    jsonLD.find(j => j['@type'] === 'HowTo') != null && jsonLD.find(j => j['@type'] === 'Organization') == null
  )) {
    jsonLD.push(ratingsJSONLD);
  }

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="image" content={image} />
      <meta name="keywords" content={config.siteKeywords} />

      {(contentNode || {}).createdAt != null && <meta property="article:published_time" content={contentNode.createdAt} />}
      {(contentNode || {}).updatedAt != null && <meta property="article:modified_time" content={contentNode.updatedAt} />}
      {(contentNode || {}).author != null && <meta property="article:author" content={contentNode.author.name} />}
      {(contentNode || {}).section != null && <meta property="article:section" content={contentNode.section} />}
      {(contentNode || {}).keywords != null && <meta property="article:tag" content={contentNode.keywords} />}

      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={contentNode != null ? 'article' : 'website'} />
      <meta property="og:site_name" content="Upsolve" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@Upsolvebk" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {robots != null && <meta name="robots" content={robots} />}

      <script type="application/ld+json">{JSON.stringify(jsonLD)}</script>
    </Helmet>
  );
}

class SEO extends Component {
  render() {
    return (
      <Location>
        {({ location }) => <SEOChild {...this.props} location={location} />}
      </Location>
    )
  }
}

export default SEO;